import { useEffect, useRef } from 'react';
import { subscribeDocCapture } from '../lib/docCaptureBus';
import type { DocStreamConnectionState } from '../lib/documentCaptureStream';
import type { DocStreamEvent } from '../types/documentCapture.types';

interface UseDocCaptureEventsOptions {
  /** Called for every event that passes `docNumber` filtering. */
  onEvent: (event: DocStreamEvent) => void;
  /** When set, only events for this document are delivered. Null/undefined = everything. */
  docNumber?: string | null;
  onConnectionState?: (state: DocStreamConnectionState) => void;
  /** Skip subscribing entirely (e.g. a panel with nothing to watch yet). */
  enabled?: boolean;
  /**
   * Coalesce a burst into ONE trailing call. Events arrive in clusters — a single decode emits
   * reading → captured → linked within a second or two — and a subscriber that refetches per event
   * would fire three identical requests for one logical change. Set this for refetch subscribers;
   * leave it 0 for reducers that must see every event individually (the capture page's journey rows).
   */
  debounceMs?: number;
}

/**
 * Subscribe a component to the shared Document Capture stream (86d3w2jrj FE-3).
 *
 * The callbacks are held in refs and the effect depends only on `docNumber`/`enabled`, so an inline
 * arrow at the call site does NOT churn the subscription on every parent render. That matters more
 * than it looks: the viewer re-renders per event once this is wired, and a subscription that
 * re-created itself each time would tear down and rebuild the shared connection in a loop — and
 * would restart any sibling interval that depends on the same identity.
 */
export function useDocCaptureEvents({
  onEvent,
  docNumber,
  onConnectionState,
  enabled = true,
  debounceMs = 0,
}: UseDocCaptureEventsOptions) {
  const onEventRef = useRef(onEvent);
  const onStateRef = useRef(onConnectionState);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    onEventRef.current = onEvent;
    onStateRef.current = onConnectionState;
  });

  useEffect(() => {
    if (!enabled) return;
    const unsubscribe = subscribeDocCapture(
      (event) => {
        // A document-level event (the link lane) carries no fileId but does carry the number, so
        // filtering on docNumber alone is right for both scan- and document-level events.
        if (docNumber && event.docNumber !== docNumber) return;
        if (debounceMs <= 0) {
          onEventRef.current(event);
          return;
        }
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
          timerRef.current = null;
          onEventRef.current(event);
        }, debounceMs);
      },
      onStateRef.current ? (state) => onStateRef.current?.(state) : undefined
    );

    return () => {
      unsubscribe();
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [docNumber, enabled, debounceMs]);
}
